import React, { useMemo } from "react";
import { Sunrise, Sun, Moon, Plus } from "lucide-react";
import { SLOTS, fmtDate } from "../../lib/planner";
import { EntryCard } from "./EntryCard";

const SLOT_ICONS = { morning: Sunrise, afternoon: Sun, night: Moon };

const SlotSection = ({ day, slot, entries, onAdd, onEdit, onToggle, onStart, isToday }) => {
  const Icon = SLOT_ICONS[slot.key];
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-ink-muted">
          <Icon className="h-3.5 w-3.5" />
          <span className="font-sans text-xs uppercase tracking-[0.1em]">{slot.label}</span>
        </div>
        <button
          onClick={() => onAdd(day, slot)}
          className="rounded-full p-1 text-ink-muted transition-colors duration-300 hover:bg-clay-soft hover:text-brand"
          data-testid={`add-${day.toLowerCase()}-${slot.key}`}
          aria-label={`Add prayer for ${day} ${slot.label}`}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {entries.length === 0 ? (
        <button
          onClick={() => onAdd(day, slot)}
          className="w-full rounded-lg border border-dashed border-[#E5E1D8] py-2.5 text-center font-sans text-xs text-ink-muted transition-colors duration-300 hover:border-brand hover:bg-clay-app"
        >
          Add a prayer
        </button>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <EntryCard key={entry.id} entry={entry} onEdit={onEdit} onToggle={onToggle} onStart={onStart} isToday={isToday} />
          ))}
        </div>
      )}
    </div>
  );
};

export const DayCard = ({ day, date, isToday, entries, index, onAdd, onEdit, onToggle, onStart }) => {
  const bySlot = useMemo(() => {
    const map = {};
    SLOTS.forEach((s) => { map[s.key] = entries.filter((e) => e.time_slot === s.key); });
    return map;
  }, [entries]);

  const doneCount = entries.filter((e) => e.completed).length;

  return (
    <div
      className={`group animate-rise rounded-2xl border p-5 shadow-[0_4px_24px_-6px_rgba(44,53,41,0.06)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(44,53,41,0.16)] ${
        isToday ? "border-brand/40 bg-gradient-to-b from-[#F6F8F3] to-white ring-1 ring-brand/15" : "border-[#E5E1D8] bg-white hover:border-[#d8d2c4]"
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`day-card-${day.toLowerCase()}`}
    >
      <div className="mb-4 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <h2 className="font-serif text-2xl text-ink">{day}</h2>
          {doneCount > 0 && (
            <span className="font-sans text-xs text-brand-success">{doneCount} done</span>
          )}
        </div>
        <span className={`font-sans text-xs ${isToday ? "rounded-full bg-brand px-2 py-0.5 text-white" : "text-ink-muted"}`}>
          {isToday ? "Today" : fmtDate(date)}
        </span>
      </div>

      <div className="space-y-4">
        {SLOTS.map((slot) => (
          <SlotSection
            key={slot.key}
            day={day}
            slot={slot}
            entries={bySlot[slot.key]}
            onAdd={onAdd}
            onEdit={onEdit}
            onToggle={onToggle}
            onStart={onStart}
            isToday={isToday}
          />
        ))}
      </div>
    </div>
  );
};
